import { useContext } from 'react'
import type { ReactNode } from 'react'
import DataContext from '../DataContext/Context'
import useDataValue from '../hooks/useDataValue'
import type { SectionAllProps } from '../../../components/Section'
import Section from '../../../components/Section'
import { FormLabel } from '../../../components'
import withComponentMarkers from '../../../shared/helpers/withComponentMarkers'

function Log({
  placeholder,
  label,
  data: logData,
  ...props
}: Omit<SectionAllProps, 'data' | 'label'> & {
  data?: unknown
  label?: ReactNode
  placeholder?: ReactNode
}) {
  const { internalDataRef } = useContext(DataContext)
  const { value: contextData } = useDataValue('/')
  const data =
    typeof contextData !== 'undefined'
      ? contextData
      : internalDataRef?.current

  return (
    <Section
      element="output"
      variant="warning"
      style={{ maxWidth: '80vw' }}
      innerSpace
      {...props}
    >
      {label && (
        <FormLabel element="span" bottom>
          <b>{label}</b>
        </FormLabel>
      )}
      <pre>
        {placeholder && Object.keys((logData ?? data) || {}).length === 0
          ? placeholder
          : JSON.stringify(
              replaceUndefinedValues(logData ?? data),
              null,
              2
            )}
        {' ' /* Ensure one line of spacing */}
      </pre>
    </Section>
  )
}

/**
 * Replaces undefined values in an object with a specified replacement value.
 * @param value - The value to check for undefined values.
 * @param replaceWith - The value to replace undefined values with. Default: `null`
 * @returns The object with undefined values replaced.
 */
function replaceUndefinedValues(
  value: unknown,
  replaceWith = 'undefined' as unknown
): unknown {
  if (typeof value === 'undefined') {
    return replaceWith
  } else if (Array.isArray(value)) {
    return value.map((item) => replaceUndefinedValues(item, replaceWith))
  } else if (value && typeof value === 'object' && value !== replaceWith) {
    return {
      ...value,
      ...Object.fromEntries(
        Object.entries(value).map(([k, v]) => [
          k,
          replaceUndefinedValues(v, replaceWith),
        ])
      ),
    }
  } else {
    return value
  }
}

withComponentMarkers(Log, { _supportsSpacingProps: true })

export default Log
